import React, { useState } from 'react';
import { User, Globe, Target, Info, Clock, ArrowRight, Check, SearchX, Loader2, Search } from 'lucide-react';
import { useSalesData } from '../context/SalesDataContext';
import { useAppendix } from '../context/AppendixContext';
import SecondSidebarItem from './common/SecondSidebarItem';

const FillButton = ({ field, value, label }) => {
    const { updateSectionData } = useSalesData();
    const [filled, setFilled] = useState(false);
    if (!value) return null;
    
    const handleFill = (e) => {
        e.stopPropagation();
        updateSectionData('SalesForm', { [field]: value }, 'zoho');
        setFilled(true);
        setTimeout(() => setFilled(false), 1500);
    };
    
    return (
        <button
            onClick={handleFill}
            title={`Enviar al formulario: ${label || field}`}
            className={`ml-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-bold transition-all duration-200
                ${filled
                    ? 'bg-emerald-500/30 text-emerald-400 border border-emerald-500/40'
                    : 'bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 hover:text-sky-300 border border-sky-500/40 hover:border-sky-500/60'
                }`}
        >
            {filled ? <Check size={8} /> : <ArrowRight size={8} />}
        </button>
    );
};

const LeadInfoSidebar = () => {
    const { sectionData, leadLoading } = useSalesData();
    const { searchLeadByPhone } = useAppendix();
    const leadData = sectionData?.LeadInfoCard || {};
    
    const hasLeadData = leadData && Object.keys(leadData).length > 0 && leadData.full_name;

    const handleOpenDeepSearch = (e) => {
        e.stopPropagation();
        if (leadData.phone) searchLeadByPhone(leadData.phone);
    };

    return (
        <SecondSidebarItem 
            id="lead-info" 
            title="Info Lead" 
            icon={User}
            actions={hasLeadData && (
                <>
                    {leadData.phone && (
                        <button
                            onClick={handleOpenDeepSearch}
                            title="Ver búsqueda profunda de este lead (campañas, contrato, adjuntos)"
                            className="p-1.5 rounded-lg bg-slate-800/60 hover:bg-primary/20 border border-white/5 hover:border-primary/40 text-slate-400 hover:text-primary transition-colors shrink-0"
                        >
                            <Search size={12} />
                        </button>
                    )}
                    {leadData.full_name && (
                        <span className="text-[9px] text-slate-400 font-medium truncate max-w-[100px] block">
                            {leadData.full_name}
                        </span>
                    )}
                </>
            )}
        >
            <div className="p-3 space-y-2.5 bg-slate-900/10 min-h-[200px]">
                {leadLoading ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                        <Loader2 size={20} className="text-primary animate-spin mb-3 opacity-80" />
                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Buscando lead...</span>
                    </div>
                ) : hasLeadData ? (
                    <>
                        {/* Nombre completo con fill button */}
                        {leadData.full_name && (
                            <div className="flex items-start gap-2.5 p-2.5 bg-slate-800/40 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
                                <div className="p-1.5 bg-blue-500/10 rounded-lg shrink-0 border border-blue-500/20">
                                    <User size={12} className="text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-[8px] font-black text-slate-500 uppercase tracking-wider mb-1">Nombre</span>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[11px] text-slate-200 font-semibold truncate leading-none">{leadData.full_name}</span>
                                        <FillButton field="full_name" value={leadData.full_name} label="Nombre" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Aglutinador */}
                        {leadData.aglutinator && (
                            <div className="flex items-start gap-2.5 p-2.5 bg-slate-800/40 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
                                <div className="p-1.5 bg-purple-500/10 rounded-lg shrink-0 border border-purple-500/20">
                                    <Target size={12} className="text-purple-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-[8px] font-black text-slate-500 uppercase tracking-wider mb-1">Aglutinador</span>
                                    <span className="text-[11px] text-slate-200 font-semibold truncate block leading-none">{leadData.aglutinator}</span>
                                </div>
                            </div>
                        )}

                        {/* Origen IT */}
                        {leadData.origen_it && (
                            <div className="flex items-start gap-2.5 p-2.5 bg-slate-800/40 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
                                <div className="p-1.5 bg-emerald-500/10 rounded-lg shrink-0 border border-emerald-500/20">
                                    <Globe size={12} className="text-emerald-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-[8px] font-black text-slate-500 uppercase tracking-wider mb-1">Origen IT</span>
                                    <span className="text-[11px] text-slate-200 font-semibold truncate block leading-none">{leadData.origen_it}</span>
                                </div>
                            </div>
                        )}

                        {/* Interés Original */}
                        {leadData.interes_original && (
                            <div className="flex items-start gap-2.5 p-2.5 bg-slate-800/40 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
                                <div className="p-1.5 bg-blue-500/10 rounded-lg shrink-0 border border-blue-500/20">
                                    <Info size={12} className="text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="block text-[8px] font-black text-slate-500 uppercase tracking-wider mb-1">Interés</span>
                                    <span className="text-[11px] text-slate-200 font-semibold block leading-relaxed">{leadData.interes_original}</span>
                                </div>
                            </div>
                        )}

                        {/* Campañas */}
                        {leadData.campaigns && leadData.campaigns.length > 0 && (
                            <div className="p-2.5 bg-slate-800/20 rounded-xl border border-white/5 flex flex-col gap-2">
                                <span className="block text-[8px] font-black text-slate-500 uppercase tracking-wider">Campañas Relevantes</span>
                                <div className="flex flex-wrap gap-1.5">
                                    {leadData.campaigns.map((camp, idx) => (
                                        <span
                                            key={idx}
                                            className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[9px] font-black rounded-md border border-blue-500/20 shadow-sm"
                                        >
                                            {camp}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Fecha Creación */}
                        {leadData.created_time && (
                            <div className="flex items-center justify-center gap-2 pt-2 border-t border-white/5 mt-2 opacity-60">
                                <Clock size={10} className="text-slate-200 shrink-0" />
                                <span className="text-[9px] text-slate-200 font-bold uppercase tracking-widest">
                                    Creado el {new Date(leadData.created_time).toLocaleDateString('es-ES')}
                                </span>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-10 px-4 text-center opacity-40">
                        <SearchX size={24} className="text-slate-500 mb-3" />
                        <span className="text-[10px] text-slate-300 font-bold uppercase tracking-widest leading-tight">No hay información del Lead</span>
                        <p className="text-[9px] text-slate-500 mt-1">Selecciona una conversación o recibe una llamada para cargar datos.</p>
                    </div>
                )}
            </div>
        </SecondSidebarItem>
    );
};

export default LeadInfoSidebar;